import { useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router';
// Local mirror of the `Comment` shape (originally declared in
// `pages/ProjectDetail/sections/AssetDetail/assetDetail.types`). Mirrored
// here because a feature MUST NOT depend on a page (FSD layer order:
// features → entities, never features → pages). The type is a thin
// projection of the generated `CommentResponseDto` with `parentId`
// narrowed — the page-side declaration remains the canonical home for
// page-internal code; this local copy keeps the feature self-contained.
import type { CommentResponseDto } from '@inference.engine/workspace-api-client/web';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import {
  commentAnnotationsQuery,
  PROJECT_COMMENT_QUERY_KEYS,
  projectCommentsInfiniteQuery,
} from '@/entities/project-comment';
import { projectFileVersionsQuery } from '@/entities/project-file';
import { notificationService } from '@/lib/services/notification.service';
import { i18n } from '@/shared/i18n';
import { useCreateComment } from '../api/useCreateComment.mutation';
import { useDeleteComment } from '../api/useDeleteComment.mutation';
import { useResolveComment } from '../api/useResolveComment.mutation';
import { useUpdateComment } from '../api/useUpdateComment.mutation';
import type {
  Annotation,
  AnnotationShape,
  AnnotationTool,
  TextSectionAnnotation,
} from './annotation';
import { FullscreenContext } from './fullscreen-context';
import { MediaPlayerProvider, useMediaPlayer } from './media-player.store';
import { parseAnnotation } from './parseAnnotation';
import { buildBriefSectionCommentIndex } from '../lib/briefSectionCommentIndex';
import type { BriefSectionCommentIndex } from '../lib/briefSectionCommentIndex';
import { selectVisibleAnnotations } from '../lib/visibleAnnotations';
import type { AnnotationVisibility } from '../lib/visibleAnnotations';
import { useTranslation } from 'react-i18next';

type Comment = Omit<CommentResponseDto, 'parentId'> & {
  parentId: string | null;
};

/**
 * Shared comment projection consumed by the target-agnostic CommentThread.
 * Exported from the feature barrel as `CommentData` so the asset page (and
 * future brief surface) can type their rows against the feature layer rather
 * than a page-local declaration (FSD forbids features → pages).
 */
export type CommentData = Comment;

// Getters, not values: this array is built while the module is evaluated, so resolving per
// read is what lets a label follow a language change instead of freezing at import.
export const ANNOTATION_STROKE_PRESETS = [
  {
    color: '#FF3B30',
    get label() {
      return i18n.t('projects:commentOnFile.commentsStore.red');
    },
  },
  {
    color: '#FFCC00',
    get label() {
      return i18n.t('projects:commentOnFile.commentsStore.yellow');
    },
  },
  {
    color: '#34C759',
    get label() {
      return i18n.t('projects:commentOnFile.commentsStore.green');
    },
  },
  {
    color: '#007AFF',
    get label() {
      return i18n.t('projects:commentOnFile.commentsStore.blue');
    },
  },
  {
    color: '#111111',
    get label() {
      return i18n.t('projects:commentOnFile.commentsStore.black');
    },
  },
] as const;

export const DEFAULT_ANNOTATION_STROKE_COLOR = ANNOTATION_STROKE_PRESETS[0].color;

/**
 * The tool primed when the comment input gains focus and no tool is
 * currently selected. Mirrors the first entry rendered in the annotation
 * toolbar's Tool row. The store itself initializes `tool` to `null` so
 * `useIsComposing` (and therefore the soloed-annotation overlay gating)
 * stays accurate while the composer is dormant.
 */
export const DEFAULT_ANNOTATION_TOOL: AnnotationTool = 'circle';

interface CommentsStoreScope {
  projectId: string;
  /** The kind of entity the comment thread is attached to. */
  targetType: 'file' | 'brief';
  /** The id of the target entity (fileId for files, briefId for briefs). */
  targetId: string;
  workspaceId: string;
  platformProjectId: string | null | undefined;
}

/**
 * URL search-param name that holds the active file version id. Source of
 * truth for `useSelectedVersionId` / `useSelectedVersion` — the store no
 * longer mirrors this so navigating, refreshing, and link-sharing always
 * land on the same version.
 */
// eslint-disable-next-line i18next/no-literal-string -- URL search param name
const SELECTED_VERSION_SEARCH_PARAM = 'version';

interface RootSortSlice {
  /**
   * Sort direction for ROOT comments only. Replies inside a thread always
   * render oldest→newest regardless of this value. In-memory only — resets
   * to `'asc'` on full page reload.
   */
  rootSortOrder: 'asc' | 'desc';
}

const ROOT_SORT_DEFAULTS: RootSortSlice = {
  rootSortOrder: 'asc',
};

interface ResolvedVisibilitySlice {
  /**
   * When false (the default), resolved root comments are filtered out by the
   * server so a brief opens showing only unresolved comments. When true,
   * they're returned with `status: 'resolved'` and rendered at reduced
   * opacity. In-memory only — resets on full page reload.
   */
  showResolved: boolean;
}

const RESOLVED_VISIBILITY_DEFAULTS: ResolvedVisibilitySlice = {
  showResolved: false,
};

interface RepliesSlice {
  /**
   * The single root comment whose reply thread is currently expanded.
   * Only one root thread can be open at a time — opening another collapses
   * the previous one. `null` when no thread is open.
   */
  openRepliesCommentId: string | null;
}

const REPLIES_DEFAULTS: RepliesSlice = {
  openRepliesCommentId: null,
};

interface SelectionSlice {
  /**
   * The single root comment whose card is currently selected. Only one card
   * can be selected at a time — selecting another switches focus. `null` when
   * nothing is selected.
   */
  selectedCommentId: string | null;
  /**
   * Transient request to scroll a comment's card into view, set by surfaces
   * outside the list (e.g. the video annotation bar). The mounted sidebar
   * consumes it via `usePendingCommentScroll` and clears it. Distinct from
   * `selectedCommentId` so that selecting a card from within the list doesn't
   * re-center it on every click.
   */
  scrollTargetCommentId: string | null;
}

const SELECTION_DEFAULTS: SelectionSlice = {
  selectedCommentId: null,
  scrollTargetCommentId: null,
};

interface ActiveComposerSlice {
  /**
   * The composer that currently owns the annotation tool selection and
   * drafts. Multiple `AnnotatedCommentInput`s may be mounted at once (the
   * bottom new-comment composer, a root edit composer), but only the
   * owner should render its annotation toolbar in the active state.
   *
   * Set on focus; never cleared on blur — annotation popovers are
   * portaled and would flicker ownership if we tied it to focus-within.
   * Cleared only when another composer claims it, on `init` (file change),
   * or explicitly via `clearActiveComposer` (e.g. after save/cancel).
   */
  activeComposerId: string | null;
}

const ACTIVE_COMPOSER_DEFAULTS: ActiveComposerSlice = {
  activeComposerId: null,
};

export interface DraftShape {
  shape: AnnotationShape;
  /** Stroke color captured at draw time — immune to later strokeColor changes. */
  color: string;
  /** PDF only: 1-indexed page this shape was drawn on. Image/video drafts omit. */
  page?: number;
}

interface AnnotationSlice {
  tool: AnnotationTool | null;
  /**
   * Drafts drawn but not yet committed to a comment. Each pointer stroke
   * appends a new entry; the last entry is the one currently being drawn
   * (if a pointer is active). Each entry carries its own color so changing
   * `strokeColor` after drawing doesn't recolor existing drafts. PDF entries
   * additionally carry the page they were drawn on so a single comment can
   * span pages.
   */
  draftShapes: DraftShape[];
  /**
   * Shapes popped by `undoDraft`, ready to be re-applied by `redoDraft`.
   * Cleared whenever the user grows `draftShapes` manually (drawing a new
   * shape) so the redo affordance never restores stale history that no
   * longer fits the current state.
   */
  redoStack: DraftShape[];
  /** Video: seconds at tool activation. */
  capturedTimestamp: number | null;
  focusedCommentId: string | null;
  /** When true, the focused comment is in edit mode — its persisted shapes are hidden from the overlay so the draft is the only version shown. */
  isFocusedEditing: boolean;
  /** When false, the draft layer is hidden from the overlay — lets the reviewer peek at the underlying asset without discarding shapes. */
  draftsVisible: boolean;
  /**
   * The comment whose annotation is currently rendered on the overlay. Null
   * means nothing is rendered. Default is null — the overlay shows an
   * annotation only when the user explicitly clicks the comment row.
   */
  soloCommentId: string | null;
  /**
   * The annotation payload for `soloCommentId`, carried here so the overlay
   * can render a reply's annotation without having to resolve it via the
   * root-only `useComments()` list.
   */
  soloAnnotation: Annotation | null;
  /**
   * The version `soloCommentId` was authored against. Always moves in lockstep
   * with `soloCommentId`/`soloAnnotation`. The overlay compares this against
   * `selectedVersionId` so a soloed annotation only renders on its source frame.
   */
  soloVersionId: string | null;
  /**
   * Transient highlight signal for deep-linked comments. Set by `flashComment`
   * and auto-cleared after 2s. Drives a one-shot CSS animation on the matching
   * row; independent of `soloCommentId` so the lasting active state and the
   * brief visual cue can coexist.
   */
  flashCommentId: string | null;
  strokeColor: string;
  /**
   * Whether the asset preview is rendered in a full-viewport Monks Dialog.
   * Toggled from `FullscreenToggle` (inline hover pill) and the close
   * button inside `FullscreenTopBar`; consumed by `AssetDetailFullScreen`
   * (which opens the Dialog and portal-reparents the preview subtree).
   * Preserved across `init` so sibling navigation from the top bar stays
   * in fullscreen — see the `isFullscreen: current.isFullscreen` carry-over
   * in `init`.
   */
  isFullscreen: boolean;
}

interface TextAnchorSlice {
  pendingTextAnchor: {
    sectionId: string;
    /**
     * The target the selection was made in, kept across `init` on the same terms as
     * `pendingFieldAnchor.targetId`. `null` scopes the anchor to the current rail session,
     * so any re-seed drops it.
     */
    targetId: string | null;
    /** Section-local ProseMirror range of the pending selection. */
    from: number;
    to: number;
    /** Plain text of the selection, captured at selection time for agent context. */
    selectedText: string;
    /** Confirms the pending mark for the created comment id (runtime decoration). */
    applyMark: (commentId: string) => void;
    cancelDraft: () => void;
  } | null;
}

const TEXT_ANCHOR_DEFAULTS: TextAnchorSlice = {
  pendingTextAnchor: null,
};

interface FieldAnchorSlice {
  /**
   * Set by "Comment on section" (`BriefSectionShell`) and consumed by the
   * bottom new-comment composer to seed `{ kind: 'field', sectionId }` on
   * save. Unlike `pendingTextAnchor` there is no draft decoration to
   * confirm/cancel — a field-level comment has nothing in the document to
   * highlight — so this is a plain value, not a set of callbacks.
   *
   * `targetId` names the target the anchor was made for. The brief rail mounts,
   * and so seeds the store, only after "Comment on section" has opened it, so
   * the anchor routinely predates the scope it belongs to; `init` keeps it
   * across that seed only when the incoming target matches.
   */
  pendingFieldAnchor: { sectionId: string; targetId: string } | null;
}

interface CommentEditSlice {
  /**
   * The comment whose edit composer is open. A selection made meanwhile re-anchors that edit,
   * so the section editor stamps it `targetId: null` rather than offering it to a new comment.
   */
  editingCommentId: string | null;
}

const COMMENT_EDIT_DEFAULTS: CommentEditSlice = {
  editingCommentId: null,
};

const FIELD_ANCHOR_DEFAULTS: FieldAnchorSlice = {
  pendingFieldAnchor: null,
};

interface LiveHighlightRange {
  commentId: string;
  from: number;
  to: number;
}

/**
 * Imperative bridge a brief section's editor exposes to the comment threads /
 * save path, so they can drive its highlight DECORATIONS without prop-drilling
 * the editor:
 *   - `setDraft`     — set/clear the in-progress pending selection.
 *   - `confirmDraft` — promote the draft to a confirmed highlight for a comment.
 *   - `upsert`       — add/replace one confirmed highlight (restore on edit-cancel).
 *   - `remove`       — drop a comment's highlight (resolve / edit-clear).
 *   - `readRanges`   — current (reflowed) ranges, for the manual-save sync.
 */
export interface BriefSectionHighlightControls {
  setDraft: (range: { from: number; to: number } | null) => void;
  confirmDraft: (commentId: string) => void;
  upsert: (highlight: LiveHighlightRange) => void;
  remove: (commentId: string) => void;
  readRanges: () => LiveHighlightRange[];
}

export const briefSectionHighlights = new Map<string, BriefSectionHighlightControls>();

type CommentsStoreState = CommentsStoreScope &
  AnnotationSlice &
  RootSortSlice &
  ResolvedVisibilitySlice &
  RepliesSlice &
  SelectionSlice &
  ActiveComposerSlice &
  TextAnchorSlice &
  FieldAnchorSlice &
  CommentEditSlice;

interface CommentsStoreActions {
  init: (params: CommentsStoreScope) => void;
  reset: () => void;
  selectTool: (tool: AnnotationTool | null, context?: { timestamp?: number }) => void;
  setDraftShapes: (shapes: DraftShape[]) => void;
  /** Pops the last draft and pushes it onto `redoStack`. No-op when empty. */
  undoDraft: () => void;
  /** Pops the last entry from `redoStack` and re-appends it to `draftShapes`. */
  redoDraft: () => void;
  /** Wipes both `draftShapes` and `redoStack` (the trash-can clear action). */
  clearDrafts: () => void;
  commitDraft: () => void;
  cancelDraft: () => void;
  focusAnnotation: (commentId: string) => void;
  clearFocus: () => void;
  beginEdit: (commentId: string, prime: { shapes: DraftShape[]; timestamp?: number }) => void;
  endEdit: () => void;
  toggleDraftsVisible: () => void;
  toggleSoloComment: (commentId: string, annotation: Annotation, versionId: string) => void;
  clearSolo: () => void;
  /** Briefly mark a comment for visual highlight; clears itself after 2s. */
  flashComment: (commentId: string) => void;
  setStrokeColor: (color: string) => void;
  /**
   * Atomic toggle of a comment's annotation overlay against its source
   * version. Show: sets the solo. Hide (same commentId): clears the solo,
   * leaves the version alone so the user can compare to a clean frame.
   * When `annotation` is null, only the solo id is tracked (no overlay) —
   * used to drive "active comment" state for comments that have no
   * annotation to replay.
   *
   * Side-effects (URL `?version=...` snap, and for video annotations the
   * seek + pause) live in `useReplayAnnotation` rather than this action
   * because react-router and the per-pane media-player store are not
   * accessible from a Zustand setter.
   */
  replayAnnotation: (commentId: string, annotation: Annotation | null, versionId: string) => void;
  /** Set the root-comment sort direction. Replies are unaffected. */
  setRootSortOrder: (order: 'asc' | 'desc') => void;
  /** Toggle whether resolved root comments are included in the list. */
  setShowResolved: (next: boolean) => void;
  setFullscreen: (value: boolean) => void;
  toggleFullscreen: () => void;
  /**
   * Open or close the reply thread for `commentId`. Opening collapses any
   * other thread that was previously open — only one can be active at a time.
   */
  toggleReplies: (commentId: string) => void;
  closeReplies: () => void;
  /**
   * Select `commentId` as the active card. Selecting another switches focus —
   * only one card is selected at a time. Selection drives the card's selected
   * visual state and reveals its reply composer.
   */
  selectComment: (commentId: string) => void;
  clearSelectedComment: () => void;
  /** Request the mounted sidebar to scroll `commentId`'s card into view. */
  requestScrollToComment: (commentId: string) => void;
  /** Clear the pending scroll request (called by the consumer once handled). */
  clearScrollTarget: () => void;
  /**
   * Force-show a comment's annotation overlay (no toggle-off). Used by
   * select-time activation, where re-selecting must always land on "shown".
   * Pass `null` to leave only the solo id tracked (no overlay).
   */
  showAnnotation: (commentId: string, annotation: Annotation | null, versionId: string) => void;
  /**
   * Mark `composerId` as the composer that owns the current annotation
   * tool selection. Called on focus from each `AnnotatedCommentInput`.
   */
  setActiveComposer: (composerId: string) => void;
  clearActiveComposer: () => void;
  setPendingTextAnchor: (anchor: TextAnchorSlice['pendingTextAnchor']) => void;
  clearPendingTextAnchor: () => void;
  setPendingFieldAnchor: (anchor: FieldAnchorSlice['pendingFieldAnchor']) => void;
  clearPendingFieldAnchor: () => void;
  startCommentEdit: (commentId: string) => void;
  /** Ends the edit only if it is still `commentId`'s, so a late end cannot cut a newer one short. */
  endCommentEdit: (commentId: string) => void;
}

const ANNOTATION_DEFAULTS: AnnotationSlice = {
  tool: null,
  draftShapes: [],
  redoStack: [],
  capturedTimestamp: null,
  focusedCommentId: null,
  isFocusedEditing: false,
  draftsVisible: true,
  soloCommentId: null,
  soloAnnotation: null,
  soloVersionId: null,
  flashCommentId: null,
  strokeColor: DEFAULT_ANNOTATION_STROKE_COLOR,
  isFullscreen: false,
};

const FLASH_DURATION_MS = 2000;

const CLEARED_SOLO = {
  soloCommentId: null,
  soloAnnotation: null,
  soloVersionId: null,
} satisfies Pick<AnnotationSlice, 'soloCommentId' | 'soloAnnotation' | 'soloVersionId'>;

const DRAFT_RESET: Pick<
  AnnotationSlice,
  'draftShapes' | 'redoStack' | 'capturedTimestamp' | 'draftsVisible'
> = {
  draftShapes: [],
  redoStack: [],
  capturedTimestamp: null,
  draftsVisible: true,
};

const useCommentsStore = create<CommentsStoreState & CommentsStoreActions>()((set, get) => ({
  projectId: '',
  targetType: 'file',
  targetId: '',
  workspaceId: '',
  platformProjectId: undefined,
  ...ANNOTATION_DEFAULTS,
  ...ROOT_SORT_DEFAULTS,
  ...RESOLVED_VISIBILITY_DEFAULTS,
  ...REPLIES_DEFAULTS,
  ...SELECTION_DEFAULTS,
  ...ACTIVE_COMPOSER_DEFAULTS,
  ...TEXT_ANCHOR_DEFAULTS,
  ...FIELD_ANCHOR_DEFAULTS,
  ...COMMENT_EDIT_DEFAULTS,
  init: (params) => {
    const current = get();
    if (
      current.projectId === params.projectId &&
      current.targetType === params.targetType &&
      current.targetId === params.targetId &&
      current.workspaceId === params.workspaceId &&
      current.platformProjectId === params.platformProjectId
    ) {
      return;
    }
    // isFullscreen is a viewer preference, not file-scoped state — preserve
    // it across file changes so the fullscreen overlay survives sibling
    // navigation from FullscreenTopBar.
    set({
      ...params,
      ...ANNOTATION_DEFAULTS,
      ...ROOT_SORT_DEFAULTS,
      ...RESOLVED_VISIBILITY_DEFAULTS,
      ...REPLIES_DEFAULTS,
      ...SELECTION_DEFAULTS,
      ...ACTIVE_COMPOSER_DEFAULTS,
      pendingTextAnchor:
        current.pendingTextAnchor?.targetId === params.targetId ? current.pendingTextAnchor : null,
      pendingFieldAnchor:
        current.pendingFieldAnchor?.targetId === params.targetId
          ? current.pendingFieldAnchor
          : null,
      ...COMMENT_EDIT_DEFAULTS,
      isFullscreen: current.isFullscreen,
    });
  },
  reset: () =>
    set({
      projectId: '',
      targetType: 'file',
      targetId: '',
      workspaceId: '',
      platformProjectId: undefined,
      ...ANNOTATION_DEFAULTS,
      ...ROOT_SORT_DEFAULTS,
      ...RESOLVED_VISIBILITY_DEFAULTS,
      ...REPLIES_DEFAULTS,
      ...SELECTION_DEFAULTS,
      ...ACTIVE_COMPOSER_DEFAULTS,
      ...COMMENT_EDIT_DEFAULTS,
    }),
  selectTool: (tool, context) =>
    set((s) => {
      // capturedTimestamp belongs to the in-flight video draft — preserve it
      // across tool toggles while drafts exist; reset when starting fresh.
      const hasDrafts = s.draftShapes.length > 0;
      const nextTimestamp =
        context && 'timestamp' in context
          ? (context.timestamp ?? null)
          : hasDrafts
            ? s.capturedTimestamp
            : null;
      if (s.tool === tool && s.capturedTimestamp === nextTimestamp) return s;
      return { tool, capturedTimestamp: nextTimestamp };
    }),
  setDraftShapes: (shapes) =>
    set((s) => {
      if (shapes === s.draftShapes) return s;
      // Manual additions (length increase) invalidate the redo stack — once
      // the user draws a new shape, the previously undone history would no
      // longer fit on top of the new state.
      const grew = shapes.length > s.draftShapes.length;
      return grew ? { draftShapes: shapes, redoStack: [] } : { draftShapes: shapes };
    }),
  undoDraft: () =>
    set((s) => {
      if (s.draftShapes.length === 0) return s;
      const last = s.draftShapes[s.draftShapes.length - 1];
      return {
        draftShapes: s.draftShapes.slice(0, -1),
        redoStack: [...s.redoStack, last],
      };
    }),
  redoDraft: () =>
    set((s) => {
      if (s.redoStack.length === 0) return s;
      const last = s.redoStack[s.redoStack.length - 1];
      return {
        draftShapes: [...s.draftShapes, last],
        redoStack: s.redoStack.slice(0, -1),
      };
    }),
  clearDrafts: () => set({ draftShapes: [], redoStack: [] }),
  commitDraft: () => set(DRAFT_RESET),
  cancelDraft: () => set(DRAFT_RESET),
  focusAnnotation: (commentId) => set({ focusedCommentId: commentId, isFocusedEditing: false }),
  clearFocus: () => set({ focusedCommentId: null, isFocusedEditing: false }),
  beginEdit: (commentId, prime) =>
    set({
      focusedCommentId: commentId,
      isFocusedEditing: true,
      tool: null,
      draftShapes: prime.shapes,
      redoStack: [],
      capturedTimestamp: prime.timestamp ?? null,
      draftsVisible: true,
    }),
  endEdit: () => set({ ...DRAFT_RESET, isFocusedEditing: false }),
  toggleDraftsVisible: () => set((s) => ({ draftsVisible: !s.draftsVisible })),
  toggleSoloComment: (commentId, annotation, versionId) =>
    set((s) =>
      s.soloCommentId === commentId
        ? CLEARED_SOLO
        : { soloCommentId: commentId, soloAnnotation: annotation, soloVersionId: versionId },
    ),
  clearSolo: () =>
    set((s) =>
      s.soloCommentId === null && s.soloAnnotation === null && s.soloVersionId === null
        ? s
        : CLEARED_SOLO,
    ),
  flashComment: (commentId) => {
    set({ flashCommentId: commentId });
    setTimeout(() => {
      // Only clear if still showing the same id — a newer flash would have
      // overwritten it, and this stale timer shouldn't cancel that one.
      if (get().flashCommentId === commentId) set({ flashCommentId: null });
    }, FLASH_DURATION_MS);
  },
  setStrokeColor: (color) => set((s) => (s.strokeColor === color ? s : { strokeColor: color })),
  setFullscreen: (value) => set((s) => (s.isFullscreen === value ? s : { isFullscreen: value })),
  toggleFullscreen: () => set((s) => ({ isFullscreen: !s.isFullscreen })),
  replayAnnotation: (commentId, annotation, versionId) => {
    if (get().soloCommentId === commentId) {
      set(CLEARED_SOLO);
      return;
    }
    set({
      soloCommentId: commentId,
      soloAnnotation: annotation,
      soloVersionId: annotation ? versionId : null,
    });
  },
  setRootSortOrder: (order) =>
    set((s) => (s.rootSortOrder === order ? s : { rootSortOrder: order })),
  setShowResolved: (next) => set((s) => (s.showResolved === next ? s : { showResolved: next })),
  toggleReplies: (commentId) =>
    set((s) => ({
      openRepliesCommentId: s.openRepliesCommentId === commentId ? null : commentId,
    })),
  closeReplies: () =>
    set((s) => (s.openRepliesCommentId === null ? s : { openRepliesCommentId: null })),
  selectComment: (commentId) =>
    set((s) => (s.selectedCommentId === commentId ? s : { selectedCommentId: commentId })),
  clearSelectedComment: () =>
    set((s) => (s.selectedCommentId === null ? s : { selectedCommentId: null })),
  requestScrollToComment: (commentId) => set({ scrollTargetCommentId: commentId }),
  clearScrollTarget: () =>
    set((s) => (s.scrollTargetCommentId === null ? s : { scrollTargetCommentId: null })),
  showAnnotation: (commentId, annotation, versionId) =>
    set({
      soloCommentId: commentId,
      soloAnnotation: annotation,
      soloVersionId: annotation ? versionId : null,
    }),
  setActiveComposer: (composerId) =>
    set((s) => (s.activeComposerId === composerId ? s : { activeComposerId: composerId })),
  clearActiveComposer: () =>
    set((s) => (s.activeComposerId === null ? s : { activeComposerId: null })),
  // A selection for a new comment and a section anchor are one pending choice, so each setter
  // displaces the other. An edit's anchor (`targetId: null`) belongs to its edit composer and
  // is left out of the exchange both ways.
  setPendingTextAnchor: (anchor) =>
    set(
      anchor?.targetId == null
        ? { pendingTextAnchor: anchor }
        : { pendingTextAnchor: anchor, pendingFieldAnchor: null },
    ),
  clearPendingTextAnchor: () =>
    set((s) => (s.pendingTextAnchor === null ? s : { pendingTextAnchor: null })),
  setPendingFieldAnchor: (anchor) => {
    const selection = get().pendingTextAnchor;
    const displaces = anchor !== null && selection !== null && selection.targetId !== null;
    // Through its own cancel, so the editor drops the draft highlight along with the anchor.
    if (displaces) selection.cancelDraft();
    set(
      displaces
        ? { pendingFieldAnchor: anchor, pendingTextAnchor: null }
        : { pendingFieldAnchor: anchor },
    );
  },
  clearPendingFieldAnchor: () =>
    set((s) => (s.pendingFieldAnchor === null ? s : { pendingFieldAnchor: null })),
  startCommentEdit: (commentId) => set({ editingCommentId: commentId }),
  endCommentEdit: (commentId) =>
    set((s) => (s.editingCommentId === commentId ? { editingCommentId: null } : s)),
}));

export const useCommentsScope = () =>
  useCommentsStore(
    useShallow((s) => ({
      projectId: s.projectId,
      targetType: s.targetType,
      targetId: s.targetId,
      workspaceId: s.workspaceId,
      platformProjectId: s.platformProjectId,
    })),
  );

export const initCommentsStore = useCommentsStore.getState().init;

export const clearCommentsStore = useCommentsStore.getState().reset;

// ── Annotation selector hooks ───────────────────────────────────────────────

export const useAnnotationTool = () =>
  useCommentsStore(
    useShallow((s) => ({
      tool: s.tool,
      selectTool: s.selectTool,
    })),
  );

export const useAnnotationDraft = () =>
  useCommentsStore(
    useShallow((s) => ({
      draftShapes: s.draftShapes,
      setDraftShapes: s.setDraftShapes,
      redoStack: s.redoStack,
      undoDraft: s.undoDraft,
      redoDraft: s.redoDraft,
      clearDrafts: s.clearDrafts,
      capturedTimestamp: s.capturedTimestamp,
      commitDraft: s.commitDraft,
      cancelDraft: s.cancelDraft,
      draftsVisible: s.draftsVisible,
      toggleDraftsVisible: s.toggleDraftsVisible,
    })),
  );

export const useAnnotationFocus = () =>
  useCommentsStore(
    useShallow((s) => ({
      focusedCommentId: s.focusedCommentId,
      isFocusedEditing: s.isFocusedEditing,
      focusAnnotation: s.focusAnnotation,
      clearFocus: s.clearFocus,
    })),
  );

export const useAnnotationEdit = () =>
  useCommentsStore(
    useShallow((s) => ({
      beginEdit: s.beginEdit,
      endEdit: s.endEdit,
    })),
  );

export const useAnnotationSolo = () =>
  useCommentsStore(
    useShallow((s) => ({
      soloCommentId: s.soloCommentId,
      soloAnnotation: s.soloAnnotation,
      soloVersionId: s.soloVersionId,
      toggleSoloComment: s.toggleSoloComment,
      clearSolo: s.clearSolo,
    })),
  );

export const useAnnotationStrokeColor = () =>
  useCommentsStore(
    useShallow((s) => ({
      strokeColor: s.strokeColor,
      setStrokeColor: s.setStrokeColor,
    })),
  );

// Both hooks always run (rules-of-hooks); the picked return value depends on
// whether a `LocalFullscreenProvider` is in the tree. Compare view uses the
// provider to scope fullscreen per pane; everywhere else, this falls back to
// the shared comments-store value.
export const useAnnotationFullscreen = () => {
  const local = useContext(FullscreenContext);
  const global = useCommentsStore(
    useShallow((s) => ({
      isFullscreen: s.isFullscreen,
      setFullscreen: s.setFullscreen,
      toggleFullscreen: s.toggleFullscreen,
    })),
  );
  return local ?? global;
};

/**
 * Snaps the rest of the page to the annotation's source frame: writes the
 * `?version=` param so preview/sidebar/overlay move in lockstep, and for video
 * annotations seeks the playhead to the timestamp and pauses so the annotation
 * reads against a stable frame. The seek/pause target the media element in the
 * current pane's `<MediaPlayerProvider>`; without one they're no-ops. No-op
 * when `annotation` is null. Shared by `useReplayAnnotation`/`useShowAnnotation`.
 */
function syncAnnotationViewport(
  annotation: Annotation | null,
  versionId: string,
  setSearchParams: ReturnType<typeof useSearchParams>[1],
  seekMedia: (timestamp: number) => void,
  pauseMedia: () => void,
) {
  if (annotation) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(SELECTED_VERSION_SEARCH_PARAM, versionId);
        return next;
      },
      { replace: true },
    );
  }
  if (annotation?.kind === 'video') {
    seekMedia(annotation.timestamp);
    pauseMedia();
  }
}

/**
 * Returns the `replayAnnotation` callback. When an annotation is provided
 * and the toggle is in the "show" direction, also snaps the page to its source
 * frame (see `syncAnnotationViewport`).
 *
 * MUST be used inside a `<MediaPlayerProvider>` — video annotations seek and
 * pause the per-pane media element, and `useMediaPlayer` throws without a
 * provider. The provider is mounted by `AssetDetail` (single-asset view) and
 * `AssetComparePane` (compare view) around any subtree that may render
 * comments or video controls.
 */
export const useReplayAnnotation = () => {
  const replayAction = useCommentsStore((s) => s.replayAnnotation);
  const { seekMedia, pauseMedia } = useMediaPlayer();
  const [, setSearchParams] = useSearchParams();

  return useCallback(
    (commentId: string, annotation: Annotation | null, versionId: string) => {
      // Only snap when this call is *opening* a solo — toggling off keeps the
      // user on the version they were comparing against.
      const isToggleOff = useCommentsStore.getState().soloCommentId === commentId;
      if (!isToggleOff) {
        syncAnnotationViewport(annotation, versionId, setSearchParams, seekMedia, pauseMedia);
      }
      replayAction(commentId, annotation, versionId);
    },
    [replayAction, setSearchParams, seekMedia, pauseMedia],
  );
};

/**
 * Force-show variant of `useReplayAnnotation`: always lands on "shown" (never
 * toggles off), snapping the page to the annotation's source frame. Drives
 * select-time annotation activation, where re-selecting a card must reliably
 * show its overlay rather than toggle it away.
 *
 * MUST be used inside a `<MediaPlayerProvider>` (same constraint as
 * `useReplayAnnotation`).
 */
export const useShowAnnotation = () => {
  const showAction = useCommentsStore((s) => s.showAnnotation);
  const { seekMedia, pauseMedia } = useMediaPlayer();
  const [, setSearchParams] = useSearchParams();

  return useCallback(
    (commentId: string, annotation: Annotation | null, versionId: string) => {
      syncAnnotationViewport(annotation, versionId, setSearchParams, seekMedia, pauseMedia);
      showAction(commentId, annotation, versionId);
    },
    [showAction, setSearchParams, seekMedia, pauseMedia],
  );
};

/**
 * Per-id boolean selector — re-renders only when *this* comment's
 * open/closed state flips. Scoped this way so toggling one root's
 * thread doesn't re-render the other N-1 CommentThreads.
 */
export const useIsRepliesOpen = (commentId: string): boolean =>
  useCommentsStore((s) => s.openRepliesCommentId === commentId);

export const useRepliesActions = () =>
  useCommentsStore(
    useShallow((s) => ({
      toggleReplies: s.toggleReplies,
      closeReplies: s.closeReplies,
    })),
  );

/**
 * Per-id boolean selector — re-renders only when *this* comment's selection
 * state flips. Scoped this way so selecting one card doesn't re-render the
 * other N-1 threads.
 */
export const useIsCommentSelected = (commentId: string): boolean =>
  useCommentsStore((s) => s.selectedCommentId === commentId);

export const useSelectedCommentId = () => useCommentsStore((s) => s.selectedCommentId);

export const useScrollTargetCommentId = () => useCommentsStore((s) => s.scrollTargetCommentId);

export const useSelectionActions = () =>
  useCommentsStore(
    useShallow((s) => ({
      selectComment: s.selectComment,
      clearSelectedComment: s.clearSelectedComment,
      requestScrollToComment: s.requestScrollToComment,
      clearScrollTarget: s.clearScrollTarget,
    })),
  );

/**
 * Per-id boolean selector — re-renders only when ownership flips for
 * *this* composer. Avoids fan-out re-renders across every mounted
 * `AnnotatedCommentInput` on focus changes.
 */
export const useIsActiveComposer = (composerId: string): boolean =>
  useCommentsStore((s) => s.activeComposerId === composerId);

export const useActiveComposerActions = () =>
  useCommentsStore(
    useShallow((s) => ({
      setActiveComposer: s.setActiveComposer,
      clearActiveComposer: s.clearActiveComposer,
    })),
  );

export const useHasActiveComposer = (): boolean =>
  useCommentsStore((s) => s.activeComposerId !== null);

export const useFlashCommentId = () => useCommentsStore((s) => s.flashCommentId);

export const useFlashComment = () => useCommentsStore((s) => s.flashComment);

export const usePendingTextAnchor = () =>
  useCommentsStore(
    useShallow((s) => ({
      pendingTextAnchor: s.pendingTextAnchor,
      setPendingTextAnchor: s.setPendingTextAnchor,
      clearPendingTextAnchor: s.clearPendingTextAnchor,
    })),
  );

export const useCommentEdit = () =>
  useCommentsStore(
    useShallow((s) => ({
      editingCommentId: s.editingCommentId,
      startCommentEdit: s.startCommentEdit,
      endCommentEdit: s.endCommentEdit,
    })),
  );

/** Read at event time by handlers that must not re-subscribe on every edit start/end. */
export const getEditingCommentId = () => useCommentsStore.getState().editingCommentId;

export const usePendingFieldAnchor = () =>
  useCommentsStore(
    useShallow((s) => ({
      pendingFieldAnchor: s.pendingFieldAnchor,
      setPendingFieldAnchor: s.setPendingFieldAnchor,
      clearPendingFieldAnchor: s.clearPendingFieldAnchor,
    })),
  );

/**
 * True when the user is actively composing (has a draft shape, has a drawing
 * tool selected, or is editing a comment). Returns a primitive so subscribers
 * don't re-render on every draft-array mutation during live drawing.
 */
export const useIsComposing = (): boolean =>
  useCommentsStore((s) => s.draftShapes.length > 0 || s.tool !== null || s.isFocusedEditing);

/**
 * True when the composer holds unsaved annotation work — drafted shapes on a
 * new comment, or a comment being edited (whose existing shapes are seeded
 * into the draft). Distinct from `useIsComposing` in that a tool being
 * selected with no shapes yet does NOT count, so other comments' annotation
 * toggles can stay enabled until the user actually starts drawing.
 */
export const useHasComposingDrafts = (): boolean =>
  useCommentsStore((s) => s.draftShapes.length > 0 || s.isFocusedEditing);

export { MediaPlayerProvider, useMediaPlayer };

// ── Selector hooks ──────────────────────────────────────────────────────────

export function useComments() {
  const projectId = useCommentsStore((s) => s.projectId);
  const targetType = useCommentsStore((s) => s.targetType);
  const targetId = useCommentsStore((s) => s.targetId);
  const { selectedVersion, isVersionResolved } = useSelectedVersion();
  const rootSortOrder = useCommentsStore((s) => s.rootSortOrder);
  const showResolved = useCommentsStore((s) => s.showResolved);
  const versionId = selectedVersion?.id;

  const {
    data,
    isLoading: isCommentsLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    projectCommentsInfiniteQuery({
      projectId,
      target: { targetType, targetId },
      versionId,
      rootSortOrder,
      showResolved,
    }),
  );

  const comments = useMemo(
    () => (data?.pages.flatMap((page) => page.items) ?? []) as Comment[],
    [data?.pages],
  );

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    comments,
    // `total` matches the active filter (pending-only when resolved are hidden),
    // so it drives pagination. `totalAll` counts every root regardless of the
    // resolved filter — use it to tell "no comments at all" from "all resolved".
    total: data?.pages[0]?.total ?? 0,
    totalAll: data?.pages[0]?.totalAll ?? 0,
    isLoading: !isVersionResolved || isCommentsLoading,
    hasMore: hasNextPage,
    isFetchingMore: isFetchingNextPage,
    loadMore,
  };
}

export const useRootSortOrder = () =>
  useCommentsStore(
    useShallow((s) => ({
      rootSortOrder: s.rootSortOrder,
      setRootSortOrder: s.setRootSortOrder,
    })),
  );

export const useShowResolved = () =>
  useCommentsStore(
    useShallow((s) => ({
      showResolved: s.showResolved,
      setShowResolved: s.setShowResolved,
    })),
  );

/**
 * Lightweight selector for the explicitly chosen version id, sourced from
 * the `?version=...` URL search param. Returns `null` when the param is
 * absent (= "use latest"). Use this when you only need the id for
 * comparison; reach for `useSelectedVersion` to get the resolved version
 * object or the versions list.
 */
export const useSelectedVersionId = () => {
  const [searchParams] = useSearchParams();
  return searchParams.get(SELECTED_VERSION_SEARCH_PARAM);
};

/**
 * Resolves the version of the active file the user is currently viewing,
 * with the `?version=...` URL search param as the source of truth. Returns
 * the explicitly selected version when one is set, otherwise falls back to
 * the latest. Versions are sorted newest-first by `createdAt`. The exposed
 * `switchToVersion(id)` writes the URL param (passing `null` removes it).
 */
export function useSelectedVersion() {
  const projectId = useCommentsStore((s) => s.projectId);
  // Versions are a file-only concept; the targetId is the fileId for file
  // scopes. For brief scopes there are no versions and this query stays empty.
  const targetType = useCommentsStore((s) => s.targetType);
  const targetId = useCommentsStore((s) => s.targetId);
  const fileId = targetType === 'file' ? targetId : undefined;
  const clearSolo = useCommentsStore((s) => s.clearSolo);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedVersionId = searchParams.get(SELECTED_VERSION_SEARCH_PARAM);

  const { data, isPending: isVersionsPending } = useQuery(
    projectFileVersionsQuery(projectId, fileId),
  );

  const versions = useMemo(() => {
    const list = data ?? [];
    return [...list].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [data]);

  const selectedVersion = useMemo(() => {
    if (!versions.length) return undefined;
    return versions.find((v) => v.id === selectedVersionId) ?? versions[0];
  }, [versions, selectedVersionId]);

  const switchToVersion = useCallback(
    (id: string | null) => {
      // Annotations are version-scoped — when the user explicitly switches
      // away, drop any solo overlay so it doesn't render on the wrong frame.
      clearSolo();
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set(SELECTED_VERSION_SEARCH_PARAM, id);
          else next.delete(SELECTED_VERSION_SEARCH_PARAM);
          return next;
        },
        { replace: true },
      );
    },
    [clearSolo, setSearchParams],
  );

  const isVersionResolved = !isVersionsPending && !!selectedVersion?.id;

  return { selectedVersion, versions, switchToVersion, isVersionResolved };
}

export function useCreateFileComment() {
  const { t } = useTranslation('projects');

  const projectId = useCommentsStore((s) => s.projectId);
  const targetId = useCommentsStore((s) => s.targetId);
  const fileTarget = useMemo(() => ({ targetType: 'file' as const, targetId }), [targetId]);
  const { selectedVersion } = useSelectedVersion();
  const targetVersionId = selectedVersion?.id;
  const createMutation = useCreateComment({
    projectId,
    target: fileTarget,
  });

  const createComment = useCallback(
    async (
      content: Record<string, unknown>,
      options?: {
        parentId?: string;
        annotation?: Annotation;
        attachmentAssetIds?: string[];
      },
    ) => {
      if (!targetVersionId) {
        notificationService.showError(t('commentOnFile.commentsStore.unableToPostComment'));
        return;
      }
      return createMutation.mutateAsync({
        content,
        versionId: targetVersionId,
        parentId: options?.parentId,
        ...(options?.annotation !== undefined
          ? { annotation: options.annotation as unknown as Record<string, unknown> }
          : {}),
        attachmentAssetIds: options?.attachmentAssetIds,
      });
    },
    [targetVersionId, createMutation, t],
  );

  return { createComment, isCreating: createMutation.isPending };
}

function useDeleteCommentForTarget(targetType: 'file' | 'brief') {
  const projectId = useCommentsStore((s) => s.projectId);
  const targetId = useCommentsStore((s) => s.targetId);
  const target = useMemo(() => ({ targetType, targetId }), [targetType, targetId]);
  const deleteMutation = useDeleteComment({ projectId, target });

  const deleteComment = useCallback(
    async (commentId: string, parentId?: string | null, replyCount?: number) => {
      await deleteMutation.mutateAsync({ commentId, parentId, replyCount });
    },
    [deleteMutation],
  );

  return { deleteComment, isDeleting: deleteMutation.isPending };
}

function useResolveCommentForTarget(targetType: 'file' | 'brief') {
  const projectId = useCommentsStore((s) => s.projectId);
  const targetId = useCommentsStore((s) => s.targetId);
  const target = useMemo(() => ({ targetType, targetId }), [targetType, targetId]);
  const resolveMutation = useResolveComment({ projectId, target });

  const resolveComment = useCallback(
    async (
      commentId: string,
      status: 'pending' | 'resolved',
      suggestionStatus?: 'applied' | 'ignored',
    ) => {
      await resolveMutation.mutateAsync({ commentId, status, suggestionStatus });
      if (targetType === 'brief' && status === 'resolved') {
        briefSectionHighlights.forEach((controls) => controls.remove(commentId));
      }
    },
    [resolveMutation, targetType],
  );

  return { resolveComment, isResolving: resolveMutation.isPending };
}

function useUpdateCommentForTarget(targetType: 'file' | 'brief') {
  const projectId = useCommentsStore((s) => s.projectId);
  const targetId = useCommentsStore((s) => s.targetId);
  const target = useMemo(() => ({ targetType, targetId }), [targetType, targetId]);
  const updateMutation = useUpdateComment({ projectId, target });

  const updateComment = useCallback(
    async (
      commentId: string,
      content: Record<string, unknown>,
      options?: { annotation?: Annotation | null; attachmentAssetIds?: string[] },
    ) => {
      await updateMutation.mutateAsync({
        commentId,
        body: {
          content,
          ...(options && 'annotation' in options
            ? { annotation: options.annotation as unknown as Record<string, unknown> | null }
            : {}),
          attachmentAssetIds: options?.attachmentAssetIds,
        },
      });
    },
    [updateMutation],
  );

  return { updateComment, isUpdating: updateMutation.isPending };
}

export const useDeleteFileComment = () => useDeleteCommentForTarget('file');

export const useResolveFileComment = () => useResolveCommentForTarget('file');

export const useUpdateFileComment = () => useUpdateCommentForTarget('file');

export function useBriefComments() {
  const projectId = useCommentsStore((s) => s.projectId);
  const targetId = useCommentsStore((s) => s.targetId);
  const rootSortOrder = useCommentsStore((s) => s.rootSortOrder);
  const showResolved = useCommentsStore((s) => s.showResolved);

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery(
    projectCommentsInfiniteQuery({
      projectId,
      target: { targetType: 'brief', targetId },
      rootSortOrder,
      showResolved,
    }),
  );

  const comments = useMemo(
    () => (data?.pages.flatMap((page) => page.items) ?? []) as Comment[],
    [data?.pages],
  );

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    comments,
    // `total` matches the active filter (pending-only when resolved are hidden),
    // so it drives pagination. `totalAll` counts every root regardless of the
    // resolved filter — use it to tell "no comments at all" from "all resolved".
    total: data?.pages[0]?.total ?? 0,
    totalAll: data?.pages[0]?.totalAll ?? 0,
    isLoading,
    hasMore: hasNextPage,
    isFetchingMore: isFetchingNextPage,
    loadMore,
  };
}

/** A comment's text-highlight annotation, paired with its comment id. */
export interface BriefCommentAnnotation {
  commentId: string;
  annotation: TextSectionAnnotation;
}

/**
 * Per-section comment counts and comment -> section lookup, across BOTH anchor kinds. The
 * selector below narrows to `kind: 'text'` because it feeds TipTap highlights; the document
 * chrome needs the wider view so a section commented on through its own affordance still
 * reports a count.
 */
export function useBriefSectionCommentIndex(
  projectId: string | undefined,
  briefId: string | undefined,
): BriefSectionCommentIndex {
  const showResolved = useCommentsStore((s) => s.showResolved);
  const visibility = useBriefAnnotationVisibility(showResolved);
  const { data } = useQuery(
    commentAnnotationsQuery(
      projectId,
      briefId ? { targetType: 'brief', targetId: briefId } : undefined,
    ),
  );

  return useMemo(() => {
    if (!Array.isArray(data)) return { countsBySection: {}, sectionByComment: {} };
    return buildBriefSectionCommentIndex(selectVisibleAnnotations(data, visibility));
  }, [data, visibility]);
}

/** The comment list as evidence about which annotations still belong to a live discussion. */
function useBriefAnnotationVisibility(showResolved: boolean): AnnotationVisibility {
  const { comments, hasMore, isLoading } = useBriefComments();

  return useMemo(
    () => ({
      showResolved,
      loadedCommentIds: comments.map((c) => c.id),
      commentListComplete: !hasMore && !isLoading,
    }),
    [showResolved, comments, hasMore, isLoading],
  );
}

export function useBriefCommentAnnotations(
  projectId: string | undefined,
  briefId: string | undefined,
) {
  const showResolved = useCommentsStore((s) => s.showResolved);
  const visibility = useBriefAnnotationVisibility(showResolved);
  const { data } = useQuery(
    commentAnnotationsQuery(
      projectId,
      briefId ? { targetType: 'brief', targetId: briefId } : undefined,
    ),
  );

  return useMemo<BriefCommentAnnotation[]>(() => {
    if (!Array.isArray(data)) return [];
    const out: BriefCommentAnnotation[] = [];
    for (const { commentId, annotation } of data) {
      const parsed = parseAnnotation(annotation);
      if (parsed?.kind === 'text' && !parsed.detached) out.push({ commentId, annotation: parsed });
    }
    return selectVisibleAnnotations(out, visibility);
  }, [data, visibility]);
}

/** One `commentAnnotations` entry for the brief PATCH (matches the SDK DTO). */
export interface BriefAnnotationUpdate {
  commentId: string;
  annotation: Record<string, unknown>;
}

/**
 * Diff the loaded annotations against the live (reflowed) highlight ranges in
 * the mounted section editors and build the `commentAnnotations` payload to
 * ship with the brief save:
 *   - moved range → reposition;
 *   - collapsed range (its text was deleted) → flag `detached: true` (the comment
 *     stays, shown as unanchored — never deleted).
 * Sections that aren't loaded are skipped. Pure — the brief PATCH applies it
 * atomically and authorized by brief write (covers other users' anchors).
 */
export function buildBriefAnnotationUpdates(
  stored: BriefCommentAnnotation[],
): BriefAnnotationUpdate[] {
  const liveById = new Map<string, { from: number; to: number }>();
  for (const controls of briefSectionHighlights.values()) {
    for (const { commentId, from, to } of controls.readRanges()) {
      liveById.set(commentId, { from, to });
    }
  }

  const updates: BriefAnnotationUpdate[] = [];
  for (const { commentId, annotation } of stored) {
    const live = liveById.get(commentId);
    if (!live) continue;
    if (live.from >= live.to) {
      updates.push({ commentId, annotation: { ...annotation, detached: true } });
    } else if (live.from !== annotation.from || live.to !== annotation.to) {
      updates.push({ commentId, annotation: { ...annotation, from: live.from, to: live.to } });
    }
  }
  return updates;
}

/**
 * Returns a callback that refreshes the brief's comment caches after a save that
 * carried annotation changes: the comment list (so detached → "Unanchored" pills
 * appear) and the annotations query (so editors re-hydrate from the new state).
 */
export function useInvalidateBriefCommentCaches(
  projectId: string | undefined,
  briefId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useCallback(() => {
    if (!projectId || !briefId) return;
    void queryClient.invalidateQueries({
      queryKey: PROJECT_COMMENT_QUERY_KEYS.list(projectId, 'brief', briefId),
    });
    void queryClient.invalidateQueries({
      queryKey: PROJECT_COMMENT_QUERY_KEYS.annotations(projectId, 'brief', briefId),
    });
  }, [queryClient, projectId, briefId]);
}

export function useCreateBriefComment() {
  const projectId = useCommentsStore((s) => s.projectId);
  const targetId = useCommentsStore((s) => s.targetId);
  const briefTarget = useMemo(() => ({ targetType: 'brief' as const, targetId }), [targetId]);
  const createMutation = useCreateComment({
    projectId,
    target: briefTarget,
  });

  const createComment = useCallback(
    async (
      content: Record<string, unknown>,
      options?: {
        parentId?: string;
        annotation?: Annotation;
        attachmentAssetIds?: string[];
      },
    ) => {
      // Returns the created DTO so callers (e.g. the brief sidebar's agent
      // thread responder) can react to the freshly posted comment.
      return await createMutation.mutateAsync({
        content,
        parentId: options?.parentId,
        ...(options?.annotation !== undefined
          ? { annotation: options.annotation as unknown as Record<string, unknown> }
          : {}),
        attachmentAssetIds: options?.attachmentAssetIds,
      });
    },
    [createMutation],
  );

  return { createComment, isCreating: createMutation.isPending };
}

export const useDeleteBriefComment = () => useDeleteCommentForTarget('brief');

export const useResolveBriefComment = () => useResolveCommentForTarget('brief');

// Brief comments carry a `text` annotation (the highlight anchor), so the brief
// update contract forwards the annotation option through to the shared updater.
export const useUpdateBriefComment = () => useUpdateCommentForTarget('brief');
